// Backend `FoodProviderCode` enum'u ile birebir.
export enum FoodProviderCode {
  YEMEKSEPETI = 'YEMEKSEPETI',
  TRENDYOL_GO = 'TRENDYOL_GO',
  GETIR = 'GETIR',
  MOCK = 'MOCK'
}

// Backend `ExternalIntegrationStatus` enum'u ile birebir.
export enum ExternalIntegrationStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  DISABLED = 'DISABLED',
  ERROR = 'ERROR'
}

// ---- Kurulum alan şeması (backend adapter'larından gelir) ----

export type ProviderFieldType =
  | 'text'
  | 'password'
  | 'number'
  | 'select'
  | 'boolean';

/** CREDENTIAL → şifreli saklanır, CONFIG → davranış ayarı (şifresiz). */
export type ProviderFieldGroup = 'CREDENTIAL' | 'CONFIG';

export const PROVIDER_FIELD_GROUP_LABELS: Record<ProviderFieldGroup, string> = {
  CREDENTIAL: 'Bağlantı Bilgileri',
  CONFIG: 'Davranış Ayarları'
};

export interface ProviderFieldOption {
  value: string;
  label: string;
}

export interface ProviderField {
  key: string;
  label: string;
  type: ProviderFieldType;
  group: ProviderFieldGroup;
  required: boolean;
  secret: boolean;
  helpText?: string;
  placeholder?: string;
  options?: ProviderFieldOption[];
  default?: string | number | boolean;
  /** Alan boşken çalışmayacak yetenekler (ör. storeId → menü/uygunluk). */
  requiredForCapabilities?: string[];
}

export interface ProviderConfigSchema {
  fields: ProviderField[];
  docsUrl?: string;
  notes?: string[];
}

export interface FoodProviderInfo {
  code: FoodProviderCode;
  capabilities: string[];
  supportsStoreDiscovery: boolean;
  configSchema: ProviderConfigSchema;
}

/** Sağlayıcı tarafındaki mağaza kaydı (kurulumda listeden seçilir). */
export interface RemoteStore {
  id: string;
  name: string;
  address?: string;
  status?: string;
  deliveryType?: string;
}

// Backend `SafeIntegration` (credential ASLA düz dönmez → yalnız hasCredentials).
export interface FoodIntegration {
  id: number;
  companyId: number;
  branchId: number;
  providerId: number;
  providerCode: FoodProviderCode;
  externalRestaurantId: string | null;
  externalVendorId: string | null;
  externalChainId: string | null;
  displayName: string | null;
  status: ExternalIntegrationStatus;
  hasCredentials: boolean;
  /** Kayıtlı credential anahtarları (değerler asla dönmez) — "kayıtlı" rozeti. */
  credentialKeys: string[];
  config?: Record<string, unknown> | null;
  isActive: boolean;
  lastHealthyAt: string | null;
  lastErrorAt: string | null;
  lastErrorMessage: string | null;
  /**
   * Son başarılı sipariş yoklaması. Trendyol GO webhook göndermediği için
   * siparişler yalnız yoklama ile gelir; bu alan "kanal yaşıyor mu" göstergesi.
   */
  lastPolledAt?: string | null;
  /** Ardışık başarısız yoklama sayısı (5'te entegrasyon ERROR'a çekilir). */
  consecutivePollFailures?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFoodIntegrationPayload {
  branchId: number;
  providerCode: FoodProviderCode;
  externalRestaurantId?: string;
  externalVendorId?: string;
  externalChainId?: string;
  displayName?: string;
  credentials?: Record<string, unknown>;
  config?: Record<string, unknown>;
}

export type UpdateFoodIntegrationPayload = Partial<
  Omit<CreateFoodIntegrationPayload, 'branchId' | 'providerCode'>
> & {
  status?: ExternalIntegrationStatus;
  isActive?: boolean;
};

export interface ValidateResult {
  ok: boolean;
  message?: string;
}

export interface HealthResult {
  ok: boolean;
  detail?: string;
}

/** Kesinti sonrası "siparişleri geri getir" sonucu. */
export interface BackfillResult {
  /** Kaç saatlik dilim sorgulandı. */
  windows: number;
  /** Platformdan çekilip işlenen paket sayısı. */
  packages: number;
  /** Taramanın başlangıç anı (ISO). */
  since: string;
}

export const FOOD_PROVIDER_LABELS: Record<FoodProviderCode, string> = {
  [FoodProviderCode.YEMEKSEPETI]: 'Yemeksepeti',
  [FoodProviderCode.TRENDYOL_GO]: 'Trendyol GO',
  [FoodProviderCode.GETIR]: 'Getir Yemek',
  [FoodProviderCode.MOCK]: 'Mock (Test)'
};

// Backend `ExternalOrderStatus` enum'u ile birebir.
export enum ExternalOrderStatus {
  RECEIVED = 'RECEIVED',
  PENDING_ACCEPTANCE = 'PENDING_ACCEPTANCE',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  PREPARING = 'PREPARING',
  READY = 'READY',
  HANDOFF = 'HANDOFF',
  DISPATCHED = 'DISPATCHED',
  DELIVERED = 'DELIVERED',
  CANCELED = 'CANCELED',
  REFUNDED = 'REFUNDED',
  FAILED = 'FAILED',
  MANUAL_REVIEW = 'MANUAL_REVIEW'
}

// Backend `ExternalWebhookStatus` enum'u ile birebir.
export enum ExternalWebhookStatus {
  RECEIVED = 'RECEIVED',
  VERIFIED = 'VERIFIED',
  REJECTED = 'REJECTED',
  DUPLICATE = 'DUPLICATE',
  ENQUEUED = 'ENQUEUED',
  FAILED = 'FAILED'
}

// Liste satırı (telefon backend'de maskeli döner).
export interface ExternalOrderRow {
  id: number;
  providerCode: FoodProviderCode;
  externalOrderId: string;
  status: ExternalOrderStatus;
  customerName: string | null;
  customerPhone: string | null;
  totalAmount: string | null;
  currency: string | null;
  internalOrderId: number | null;
  receivedAt: string;
  lastEventAt: string | null;
  itemCount: number;
  /** Operatör "platform iptalini onayla" işlemini yaptı mı (CANCEL_ACK olayı var mı). */
  cancelAcked: boolean;
  /** Müşterinin gördüğü kısa sipariş numarası (externalOrderId 64 karakterdir). */
  orderNumber: string | null;
  /** PAY_WITH_CARD | PAY_WITH_ON_DELIVERY — kapıda ödemede kurye tahsil eder. */
  paymentType: string | null;
  /** Kapıda ödeme yöntemi (CASH/CARD/…) veya yemek kartı adı. */
  paymentMethod: string | null;
  /** Gel-al sipariş: kurye çıkmaz, müşteri şubeden alır. */
  isPickup: boolean;
  /** Sağlayıcının test siparişi: ciroya ve stok hareketine girmez. */
  isTestOrder: boolean;
  /** STORE = kendi kuryesi (Model 1), GO = platform kuryesi (Model 2). */
  deliveryModel: string | null;
}

/** Müşterinin bir kalemde değiştirdiği içerik (ekleme/çıkarma). */
export interface IngredientChange {
  id?: string;
  name: string;
  price?: number;
}

export interface ExternalOrderItem {
  id: number;
  externalOrderId: number;
  companyProductId: number | null;
  externalProductId: string | null;
  name: string;
  quantity: number;
  unitPrice: string | null;
  totalPrice: string | null;
  modifiers: unknown;
  /** "Ekstra peynir" gibi eklemeler — mutfağa iletilmelidir. */
  addedIngredients: IngredientChange[] | null;
  /** "Soğansız" gibi çıkarmalar — iletilmemesi iade nedenidir (TGO 4014). */
  removedIngredients: IngredientChange[] | null;
  raw: unknown;
  createdAt: string;
}

export interface ExternalOrderEvent {
  id: number;
  externalOrderId: number;
  sequence: number | null;
  eventType: string | null;
  status: ExternalOrderStatus | null;
  occurredAt: string | null;
  outcome: string | null;
  webhookLogId: number | null;
  createdAt: string;
}

export interface ExternalOrderDetail extends ExternalOrderRow {
  integrationId: number;
  companyId: number;
  branchId: number;
  /** Platforma gönderilen fatura bağlantısı (Faz 5). */
  invoiceLink: string | null;
  invoiceSentAt: string | null;
  lastSequence: number | null;
  deliveryAddress: string | null;
  /** SİSTEM notu (red nedeni, eşleşmeyen kalem) — müşteri notu değildir. */
  notes: string | null;
  /** Müşterinin siparişe yazdığı not. */
  customerNote: string | null;
  /** Alınan teslimat ücreti (Model 1 siparişlerde dolu olabilir). */
  deliveryFee: string | null;
  items: ExternalOrderItem[];
  events: ExternalOrderEvent[];
}

export interface Paginated<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ExternalWebhookLog {
  id: number;
  integrationId: number | null;
  providerCode: FoodProviderCode;
  eventId: string | null;
  eventType: string | null;
  status: ExternalWebhookStatus;
  signatureValid: boolean | null;
  errorMessage: string | null;
  receivedAt: string;
  processedAt: string | null;
}

// Türkçe rozet etiketleri — panelde ham enum değeri GÖSTERİLMEZ.
export const EXTERNAL_ORDER_STATUS_LABELS: Record<ExternalOrderStatus, string> =
  {
    [ExternalOrderStatus.RECEIVED]: 'Yeni Sipariş',
    [ExternalOrderStatus.PENDING_ACCEPTANCE]: 'Onay Bekliyor',
    [ExternalOrderStatus.ACCEPTED]: 'Kabul Edildi',
    [ExternalOrderStatus.REJECTED]: 'Reddedildi',
    [ExternalOrderStatus.PREPARING]: 'Hazırlanıyor',
    [ExternalOrderStatus.READY]: 'Hazır',
    [ExternalOrderStatus.HANDOFF]: 'Kuryeye Verildi',
    [ExternalOrderStatus.DISPATCHED]: 'Kurye Yolda',
    [ExternalOrderStatus.DELIVERED]: 'Teslim Edildi',
    [ExternalOrderStatus.CANCELED]: 'İptal Edildi',
    [ExternalOrderStatus.REFUNDED]: 'İade Edildi',
    [ExternalOrderStatus.FAILED]: 'Hata',
    [ExternalOrderStatus.MANUAL_REVIEW]: 'İnceleme Gerekli'
  };

/**
 * Ödeme tipi etiketleri. Sağlayıcı kodları sabit değil (yeni yöntem eklenebilir)
 * → `Record<string, string>` ve bilinmeyen kod ham gösterilir, gizlenmez.
 */
export const EXTERNAL_PAYMENT_TYPE_LABELS: Record<string, string> = {
  PAY_WITH_CARD: 'Online ödendi',
  PAY_WITH_ON_DELIVERY: 'Kapıda ödeme'
};

/** Kapıda ödeme alt yöntemleri (Trendyol GO "Paket Modelleri" listesi). */
export const EXTERNAL_PAYMENT_METHOD_LABELS: Record<string, string> = {
  CASH: 'Nakit',
  CARD: 'Kredi kartı',
  SODEXO_CARD: 'Pluxee kart',
  SODEXO_CODE: 'Pluxee kod',
  MULTINET_CARD: 'Multinet kart',
  MULTINET_CODE: 'Multinet kod',
  EDENRED_CARD: 'Edenred kart',
  EDENRED_CODE: 'Edenred kod',
  SETCARD_CARD: 'Setcard kart',
  SETCARD_CODE: 'Setcard kod',
  METROPOL_CARD: 'MetropolCard kart',
  METROPOL_CODE: 'MetropolCard kod',
  TOKENFLEX_MOBILE: 'Tokenflex mobil',
  TOKENFLEX_CODE: 'Tokenflex kod',
  PAYEKART_CARD: 'Paye kart',
  PAYEKART_CODE: 'Paye kod',
  IWALLET_CODE: 'iWallet kod'
};

/** Teslimat modeli: kendi kuryesi mi platform kuryesi mi. */
export const EXTERNAL_DELIVERY_MODEL_LABELS: Record<string, string> = {
  STORE: 'Kendi kuryemiz',
  GO: 'Platform kuryesi'
};

export const EXTERNAL_INTEGRATION_STATUS_LABELS: Record<
  ExternalIntegrationStatus,
  string
> = {
  [ExternalIntegrationStatus.PENDING]: 'Kurulum Bekliyor',
  [ExternalIntegrationStatus.ACTIVE]: 'Aktif',
  [ExternalIntegrationStatus.PAUSED]: 'Duraklatıldı',
  [ExternalIntegrationStatus.DISABLED]: 'Devre Dışı',
  [ExternalIntegrationStatus.ERROR]: 'Hata'
};

export const EXTERNAL_WEBHOOK_STATUS_LABELS: Record<
  ExternalWebhookStatus,
  string
> = {
  [ExternalWebhookStatus.RECEIVED]: 'Alındı',
  [ExternalWebhookStatus.VERIFIED]: 'Doğrulandı',
  [ExternalWebhookStatus.REJECTED]: 'Reddedildi',
  [ExternalWebhookStatus.DUPLICATE]: 'Mükerrer',
  [ExternalWebhookStatus.ENQUEUED]: 'Kuyruğa Alındı',
  [ExternalWebhookStatus.FAILED]: 'Başarısız'
};

// Olay günlüğü `outcome` alanı (backend StageOutcome + event sonuçları).
export const EXTERNAL_EVENT_OUTCOME_LABELS: Record<string, string> = {
  APPLIED: 'Uygulandı',
  CREATED: 'Oluşturuldu',
  OUT_OF_ORDER_SKIPPED: 'Sırasız — Atlandı',
  TERMINAL_GUARD: 'Kapalı Statü — Yoksayıldı',
  ALREADY_CONVERTED: 'Zaten Dönüştürülmüş',
  MANUAL_REVIEW: 'İnceleme Gerekli',
  CONVERTED: 'Mutfağa Aktarıldı',
  CANCEL_ACK: 'İptal Onaylandı'
};

export const EXTERNAL_ORDER_STATUS_COLOR: Record<
  ExternalOrderStatus,
  'default' | 'success' | 'warning' | 'error' | 'info' | 'primary'
> = {
  [ExternalOrderStatus.RECEIVED]: 'info',
  [ExternalOrderStatus.PENDING_ACCEPTANCE]: 'warning',
  [ExternalOrderStatus.ACCEPTED]: 'primary',
  [ExternalOrderStatus.REJECTED]: 'error',
  [ExternalOrderStatus.PREPARING]: 'primary',
  [ExternalOrderStatus.READY]: 'success',
  [ExternalOrderStatus.HANDOFF]: 'success',
  [ExternalOrderStatus.DISPATCHED]: 'info',
  [ExternalOrderStatus.DELIVERED]: 'success',
  [ExternalOrderStatus.CANCELED]: 'default',
  [ExternalOrderStatus.REFUNDED]: 'default',
  [ExternalOrderStatus.FAILED]: 'error',
  [ExternalOrderStatus.MANUAL_REVIEW]: 'warning'
};

// ---- Faz 6: menü içe aktarma + ürün/modifier eşleştirme ----

// Backend `ExternalMappingSource` enum'u ile birebir.
export enum ExternalMappingSource {
  MANUAL = 'MANUAL',
  SUGGESTED = 'SUGGESTED',
  AUTO = 'AUTO'
}

// Bir external ürün için tek aday core ürün + güven skoru.
export interface ProductMappingCandidate {
  companyProductId: number;
  name: string;
  basePrice: number;
  categoryName: string | null;
  confidence: number;
}

// Bir external ürünün eşleştirme önerisi (persist edilmez — onaya sunulur).
export interface ProductMappingSuggestion {
  externalProductId: string;
  externalProductName: string;
  externalCategoryName: string | null;
  externalPrice: number | null;
  alreadyMapped: boolean;
  mappedCompanyProductId: number | null;
  candidates: ProductMappingCandidate[];
  bestConfidence: number;
  autoSuggest: boolean;
}

export interface MenuImportResult {
  snapshotId: number;
  itemCount: number;
  categoryCount: number;
  modifierCount: number;
  suggestions: ProductMappingSuggestion[];
}

// Kaydedilmiş ürün eşleşmesi (companyProduct join'li döner).
export interface ExternalProductMapping {
  id: number;
  integrationId: number;
  externalProductId: string;
  companyProductId: number;
  externalProductName: string | null;
  externalCategoryName: string | null;
  platformPrice: string | null;
  platformAvailable: boolean;
  confidence: number | null;
  source: ExternalMappingSource;
  isActive: boolean;
  // Faz 7: platforma en son gönderilen değerler (drift göstergesi).
  lastSyncedPrice: string | null;
  lastSyncedAvailable: boolean | null;
  lastSyncedAt: string | null;
  companyProduct?: {
    id: number;
    name: string;
    basePrice: number;
    status: string;
  };
}

export interface ExternalModifierMapping {
  id: number;
  integrationId: number;
  externalModifierId: string;
  externalProductId: string | null;
  externalModifierName: string | null;
  attributeOptionId: number;
  platformPrice: string | null;
  confidence: number | null;
  source: ExternalMappingSource;
  isActive: boolean;
  attributeOption?: {
    id: number;
    name: string;
    extraPrice: number;
    attribute: { id: number; name: string; productId: number };
  };
}

export interface UpsertProductMappingPayload {
  externalProductId: string;
  companyProductId: number;
  externalProductName?: string;
  externalCategoryName?: string;
  platformPrice?: number;
  platformAvailable?: boolean;
  confidence?: number;
  source?: ExternalMappingSource;
}

export interface UpsertModifierMappingPayload {
  externalModifierId: string;
  attributeOptionId: number;
  externalProductId?: string;
  externalModifierName?: string;
  platformPrice?: number;
  confidence?: number;
  source?: ExternalMappingSource;
}

// ---- Faz 7: fiyat/availability senkronu ----

// Backend `ExternalSyncType` enum'u ile birebir.
export enum ExternalSyncType {
  PRICE = 'PRICE',
  AVAILABILITY = 'AVAILABILITY'
}

// Backend `ExternalSyncTrigger` enum'u ile birebir.
export enum ExternalSyncTrigger {
  MANUAL = 'MANUAL',
  SCHEDULED = 'SCHEDULED',
  STOCK_EVENT = 'STOCK_EVENT'
}

// Backend `ExternalSyncStatus` enum'u ile birebir.
export enum ExternalSyncStatus {
  SUCCESS = 'SUCCESS',
  PARTIAL = 'PARTIAL',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED',
  NOOP = 'NOOP',
  // Fiyat güncellemesi sağlayıcıya iletildi ama sonucu henüz bilinmiyor:
  // Trendyol GO fiyatı kuyruğa alır, sonuç ayrı bir sorguyla öğrenilir.
  PENDING = 'PENDING'
}

// Tek senkron tipinin (PRICE/AVAILABILITY) çalışma özeti (backend SyncTypeResult).
export interface SyncTypeResult {
  type: ExternalSyncType;
  status: ExternalSyncStatus;
  itemsChecked: number;
  itemsDrifted: number;
  itemsPushed: number;
  itemsFailed: number;
}

export interface SyncRunResult {
  integrationId: number;
  results: SyncTypeResult[];
}

// Senkron çalışma günlüğü satırı (backend `ExternalSyncLog`).
export interface ExternalSyncLog {
  id: number;
  integrationId: number;
  providerCode: FoodProviderCode;
  type: ExternalSyncType;
  trigger: ExternalSyncTrigger;
  status: ExternalSyncStatus;
  itemsChecked: number;
  itemsDrifted: number;
  itemsPushed: number;
  itemsFailed: number;
  durationMs: number | null;
  errorMessage: string | null;
  createdAt: string;
}

export const EXTERNAL_MAPPING_SOURCE_LABELS: Record<
  ExternalMappingSource,
  string
> = {
  [ExternalMappingSource.MANUAL]: 'Elle',
  [ExternalMappingSource.SUGGESTED]: 'Öneriden Onaylandı',
  [ExternalMappingSource.AUTO]: 'Otomatik'
};

export const EXTERNAL_SYNC_TYPE_LABELS: Record<ExternalSyncType, string> = {
  [ExternalSyncType.PRICE]: 'Fiyat',
  [ExternalSyncType.AVAILABILITY]: 'Uygunluk'
};

export const EXTERNAL_SYNC_TRIGGER_LABELS: Record<ExternalSyncTrigger, string> =
  {
    [ExternalSyncTrigger.MANUAL]: 'Elle',
    [ExternalSyncTrigger.SCHEDULED]: 'Zamanlanmış',
    [ExternalSyncTrigger.STOCK_EVENT]: 'Stok Olayı'
  };

export const EXTERNAL_SYNC_STATUS_LABELS: Record<ExternalSyncStatus, string> = {
  [ExternalSyncStatus.SUCCESS]: 'Başarılı',
  [ExternalSyncStatus.PARTIAL]: 'Kısmen Başarılı',
  [ExternalSyncStatus.FAILED]: 'Başarısız',
  [ExternalSyncStatus.SKIPPED]: 'Atlandı',
  [ExternalSyncStatus.NOOP]: 'Değişiklik Yok',
  [ExternalSyncStatus.PENDING]: 'Platformda İşleniyor'
};

export const EXTERNAL_SYNC_STATUS_COLOR: Record<
  ExternalSyncStatus,
  'default' | 'success' | 'warning' | 'error' | 'info'
> = {
  [ExternalSyncStatus.SUCCESS]: 'success',
  [ExternalSyncStatus.PARTIAL]: 'warning',
  [ExternalSyncStatus.FAILED]: 'error',
  [ExternalSyncStatus.SKIPPED]: 'default',
  [ExternalSyncStatus.NOOP]: 'info',
  [ExternalSyncStatus.PENDING]: 'info'
};

export const EXTERNAL_WEBHOOK_STATUS_COLOR: Record<
  ExternalWebhookStatus,
  'default' | 'success' | 'warning' | 'error' | 'info'
> = {
  [ExternalWebhookStatus.RECEIVED]: 'info',
  [ExternalWebhookStatus.VERIFIED]: 'success',
  [ExternalWebhookStatus.REJECTED]: 'error',
  [ExternalWebhookStatus.DUPLICATE]: 'warning',
  [ExternalWebhookStatus.ENQUEUED]: 'info',
  [ExternalWebhookStatus.FAILED]: 'error'
};

// ---- Faz 8: restoran operasyonları (açık/kapalı/yoğun, saat, süre) ----

// Backend `RestaurantStatus` ile birebir.
export type RestaurantStatus = 'OPEN' | 'CLOSED' | 'BUSY';

export const RESTAURANT_STATUS_LABELS: Record<RestaurantStatus, string> = {
  OPEN: 'Açık',
  CLOSED: 'Kapalı',
  BUSY: 'Yoğun'
};

export const RESTAURANT_STATUS_COLOR: Record<
  RestaurantStatus,
  'default' | 'success' | 'warning' | 'error'
> = {
  OPEN: 'success',
  CLOSED: 'default',
  BUSY: 'warning'
};

// Backend `WorkingHoursInterval` (dayOfWeek: 0=Pazar … 6=Cumartesi).
export interface WorkingHoursInterval {
  dayOfWeek: number;
  opensAt: string;
  closesAt: string;
}

// Backend `RestaurantOpsView` (GET /operations).
export interface RestaurantOpsView {
  supported: boolean;
  workingHoursSupported: boolean;
  deliveryDurationSupported: boolean;
  opsEnabled: boolean;
  status?: RestaurantStatus;
  statusUpdatedAt?: string;
  /** Platformdaki GERÇEK durum (taze okundu); okunamadıysa undefined. */
  platformStatus?: RestaurantStatus;
  /** Platform durumu okunamadıysa nedeni. */
  platformStatusError?: string;
  /** Yerel kayıt ile platform arasında fark var mı. */
  platformStatusMismatch?: boolean;
  workingHours?: WorkingHoursInterval[];
  workingHoursUpdatedAt?: string;
  deliveryDurationMinutes?: number;
  deliveryDurationUpdatedAt?: string;
}

// Backend `OperationResult` (push sonucu).
export interface OperationResult {
  outcome: 'SUCCESS' | 'SKIPPED' | 'FAILED';
  reason?: string;
}

// ---- Faz 10: monitoring (sağlık özeti + metrik + alert) ----

export type FoodAlertSeverity = 'warning' | 'critical';

export type FoodAlertCode =
  | 'INTEGRATION_ERROR'
  | 'DEAD_LETTER_BACKLOG'
  | 'MANUAL_REVIEW_BACKLOG'
  | 'WEBHOOK_FAILURE_RATE'
  | 'INTEGRATION_SILENT'
  | 'POLLING_STALLED'
  | 'CLAIM_ACTION_REQUIRED';

export const FOOD_ALERT_CODE_LABELS: Record<FoodAlertCode, string> = {
  INTEGRATION_ERROR: 'Entegrasyon Hatası',
  DEAD_LETTER_BACKLOG: 'İşlenemeyen Bildirim Birikimi',
  MANUAL_REVIEW_BACKLOG: 'İnceleme Bekleyen Sipariş Birikimi',
  WEBHOOK_FAILURE_RATE: 'Bildirim Hata Oranı Yüksek',
  INTEGRATION_SILENT: 'Entegrasyondan Ses Yok',
  POLLING_STALLED: 'Sipariş Sorgulaması Durdu',
  CLAIM_ACTION_REQUIRED: 'İade Yanıt Süresi Doluyor'
};

export interface FoodAlert {
  code: FoodAlertCode;
  severity: FoodAlertSeverity;
  message: string;
  integrationId?: number;
  value?: number;
}

export interface IntegrationHealth {
  id: number;
  providerCode: FoodProviderCode;
  displayName: string | null;
  status: ExternalIntegrationStatus;
  isActive: boolean;
  lastHealthyAt: string | null;
  lastErrorAt: string | null;
  lastErrorMessage: string | null;
  lastWebhookAt: string | null;
  /**
   * Son BAŞARILI sipariş yoklaması. Trendyol GO webhook GÖNDERMEZ; o
   * platformda canlılığın tek göstergesi budur (`lastWebhookAt` hep boş kalır).
   */
  lastPolledAt: string | null;
  /** Entegrasyonun yoklama aralığı (saniye) — "yoklama durdu" eşiği için. */
  pollIntervalSeconds: number;
}

export interface BranchHealthOverview {
  branchId: number;
  generatedAt: string;
  summary: {
    total: number;
    active: number;
    error: number;
    paused: number;
    pending: number;
    disabled: number;
  };
  integrations: IntegrationHealth[];
}

export interface OrderFunnel {
  total: number;
  received: number;
  converted: number;
  manualReview: number;
  rejected: number;
  canceled: number;
  failed: number;
}

export interface WebhookHealth {
  total: number;
  received: number;
  verified: number;
  rejected: number;
  duplicate: number;
  enqueued: number;
  failed: number;
}

export interface BranchMetrics {
  branchId: number;
  windowHours: number;
  since: string;
  generatedAt: string;
  orders: OrderFunnel;
  conversionRate: number | null;
  webhooks: WebhookHealth;
  webhookFailureRate: number | null;
  deadLetterBacklog: number;
  manualReviewBacklog: number;
  /** Aksiyon bekleyen müşteri iadeleri (4 saatlik yanıt süresi). */
  claims: ClaimBacklog;
  sync: {
    lastStatus: ExternalSyncStatus | null;
    lastAt: string | null;
    failedCount: number;
  };
}

// Backend `/food-delivery/alerts` yanıtı (overview + metrics + alerts).
export interface BranchMonitoring {
  overview: BranchHealthOverview;
  metrics: BranchMetrics;
  alerts: FoodAlert[];
}

export const FOOD_ALERT_SEVERITY_COLOR: Record<
  FoodAlertSeverity,
  'warning' | 'error'
> = {
  warning: 'warning',
  critical: 'error'
};

// ─── Faz 11 (ürünleştirme/ölçekleme): çok-şube (company-wide) + toplu yönetim ───

export interface BranchStatusSummary {
  branchId: number;
  total: number;
  active: number;
  error: number;
  paused: number;
  pending: number;
  disabled: number;
}

// Backend `/food-delivery/company/health` yanıtı.
export interface CompanyHealthOverview {
  companyId: number;
  generatedAt: string;
  summary: {
    branches: number;
    total: number;
    active: number;
    error: number;
    paused: number;
    pending: number;
    disabled: number;
  };
  branches: BranchStatusSummary[];
  integrations: IntegrationHealth[];
}

// Backend `/food-delivery/company/metrics` yanıtı.
export interface CompanyMetrics {
  companyId: number;
  windowHours: number;
  since: string;
  generatedAt: string;
  orders: OrderFunnel;
  conversionRate: number | null;
  webhooks: WebhookHealth;
  webhookFailureRate: number | null;
  deadLetterBacklog: number;
  manualReviewBacklog: number;
  /** Aksiyon bekleyen müşteri iadeleri (4 saatlik yanıt süresi). */
  claims: ClaimBacklog;
  sync: {
    lastStatus: ExternalSyncStatus | null;
    lastAt: string | null;
    failedCount: number;
  };
}

// Backend `/food-delivery/company/alerts` yanıtı.
export interface CompanyMonitoring {
  overview: CompanyHealthOverview;
  metrics: CompanyMetrics;
  alerts: FoodAlert[];
}

// Toplu entegrasyon yönetimi aksiyonları (backend BulkIntegrationAction ile birebir).
export enum BulkIntegrationAction {
  PAUSE = 'PAUSE',
  RESUME = 'RESUME',
  DISABLE = 'DISABLE',
  REVALIDATE = 'REVALIDATE'
}

export interface BulkActionResult {
  action: BulkIntegrationAction;
  requested: number;
  succeeded: number[];
  failed: { id: number; reason: string }[];
  notFound: number[];
}

// ---- Müşteri iadesi (claim) — backend Faz 5 ----

/** Aksiyon bekleyen iadelerin SLA özeti (monitoring metriklerinde taşınır). */
export interface ClaimBacklog {
  open: number;
  /** Son yanıt tarihine 1 saatten az kalanlar. */
  dueSoon: number;
  /** Süresi geçmiş, hâlâ yanıtlanmamış iadeler. */
  overdue: number;
  nextDeadlineAt: string | null;
}

// Backend `ExternalClaimStatus` enum'u ile birebir.
export enum ExternalClaimStatus {
  WAITING_IN_ACTION = 'WAITING_IN_ACTION',
  WAITING_FOR_SELLER_REFUND = 'WAITING_FOR_SELLER_REFUND',
  UNRESOLVED = 'UNRESOLVED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  UNKNOWN = 'UNKNOWN'
}

// Backend `ExternalClaimRefundType` enum'u ile birebir.
export enum ExternalClaimRefundType {
  PAY_BACK = 'PAY_BACK',
  PRODUCT_RESEND = 'PRODUCT_RESEND',
  UNKNOWN = 'UNKNOWN'
}

export const EXTERNAL_CLAIM_STATUS_LABELS: Record<ExternalClaimStatus, string> =
  {
    [ExternalClaimStatus.WAITING_IN_ACTION]: 'Yanıtınız Bekleniyor',
    [ExternalClaimStatus.WAITING_FOR_SELLER_REFUND]: 'Telafi Bekleniyor',
    [ExternalClaimStatus.UNRESOLVED]: 'Reddedildi (Platform İnceliyor)',
    [ExternalClaimStatus.ACCEPTED]: 'Kabul Edildi',
    [ExternalClaimStatus.REJECTED]: 'Platform Reddetti',
    [ExternalClaimStatus.UNKNOWN]: 'Sonuçlandı (Ayrıntı Platformda)'
  };

export const EXTERNAL_CLAIM_STATUS_COLOR: Record<
  ExternalClaimStatus,
  'default' | 'success' | 'warning' | 'error' | 'info'
> = {
  [ExternalClaimStatus.WAITING_IN_ACTION]: 'warning',
  [ExternalClaimStatus.WAITING_FOR_SELLER_REFUND]: 'warning',
  [ExternalClaimStatus.UNRESOLVED]: 'info',
  [ExternalClaimStatus.ACCEPTED]: 'success',
  [ExternalClaimStatus.REJECTED]: 'error',
  [ExternalClaimStatus.UNKNOWN]: 'default'
};

export const EXTERNAL_CLAIM_REFUND_TYPE_LABELS: Record<
  ExternalClaimRefundType,
  string
> = {
  [ExternalClaimRefundType.PAY_BACK]: 'Para İadesi',
  [ExternalClaimRefundType.PRODUCT_RESEND]: 'Ürün Telafisi',
  [ExternalClaimRefundType.UNKNOWN]: 'Belirtilmemiş'
};

/** İade kalemi (backend `items` JSON alanı). */
export interface ExternalClaimItem {
  id: string;
  lineItemId?: string;
  status?: string;
  note?: string;
  price?: number;
  /** Müşterinin iade nedeni kodu (4000-4022, 451). */
  reasonId?: number;
  reasonName?: string;
  imageUrls?: string[];
}

/** Backend `ExternalClaim` satırı (ham `raw` alanı dönmez). */
export interface ExternalClaim {
  id: number;
  integrationId: number;
  companyId: number;
  branchId: number;
  providerCode: FoodProviderCode;
  externalClaimId: string;
  externalOrderId: string | null;
  orderNumber: string | null;
  packageId: string | null;
  status: ExternalClaimStatus;
  /** "Standard" = tam iade, "Manual" = kalem bazında kısmi iade. */
  type: string | null;
  refundType: ExternalClaimRefundType;
  totalPrice: string | null;
  items: ExternalClaimItem[] | null;
  claimCreatedAt: string | null;
  actionDeadlineAt: string | null;
  completedAt: string | null;
  actionedByUserId: number | null;
  actionedAt: string | null;
  unresolveReasonId: number | null;
  unresolveNote: string | null;
  createdAt: string;
  updatedAt: string;
  /** Son yanıt tarihine kalan süre (ms); negatifse süre geçmiş. */
  remainingMs: number | null;
}

/** İade neden tabloları (backend sabit listeden döner). */
export interface ClaimReasonTables {
  customerReasons: { id: number; label: string }[];
  unresolveReasons: { id: number; label: string }[];
}

// ---- Faz 5: kategori / fatura / değerlendirme ----

/** Platform menü kategorisi (backend `ExternalMenuCategory`). */
export interface ExternalMenuCategory {
  id: number;
  integrationId: number;
  companyId: number;
  branchId: number;
  providerCode: FoodProviderCode;
  externalCategoryId: string;
  name: string | null;
  position: number | null;
  isAvailable: boolean;
  lastSeenAt: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Restoran puan ortalamaları. Yeterli değerlendirme yoksa `available:false`. */
export interface ProviderRatingSummary {
  available: boolean;
  unavailableReason?: string;
  overall?: number;
  flavor?: number;
  service?: number;
  delivery?: number;
  commentCount?: number;
  ratingCount?: number;
}

/** Restoranın yoruma verdiği cevap; platform onayından geçer. */
export interface ProviderReviewAnswer {
  text?: string;
  /** WAITING_FOR_APPROVE | APPROVED | REJECTED */
  status?: string;
  rejectedReason?: string;
  rejectedReasonId?: number;
}

export interface ProviderReview {
  reviewId: string;
  orderNumber?: string;
  createdAt?: string;
  overallScore?: number;
  flavorScore?: number;
  serviceScore?: number;
  deliveryScore?: number;
  comment?: string;
  productNames: string[];
  answer?: ProviderReviewAnswer;
}

export const REVIEW_ANSWER_STATUS_LABELS: Record<string, string> = {
  WAITING_FOR_APPROVE: 'Onay Bekliyor',
  APPROVED: 'Yayında',
  REJECTED: 'Reddedildi'
};

export const REVIEW_ANSWER_STATUS_COLOR: Record<
  string,
  'default' | 'success' | 'warning' | 'error'
> = {
  WAITING_FOR_APPROVE: 'warning',
  APPROVED: 'success',
  REJECTED: 'error'
};
